import { z } from 'zod';
import { addressSchema } from '../../types/Address.js';
import { dateOfBirthSchema } from '../common/dateOfBirth.js';
import { emailSchema } from '../common/email.js';
import { annualIncomeSchema } from '../common/financial.js';
import { personNameSchema, personLastNameSchema } from '../common/name.js';
import { phoneSchema } from '../common/phone.js';
import { ssnSchema } from '../common/ssn.js';
import { tcpaConsentMetadataSchema } from '../common/tcpa.js';

// Express checkout sends an empty string when the buyer has no middle name, so
// the empty value is accepted alongside a real one.
const middleNameSchema = z.union([personNameSchema, z.literal('')]);
/**
 * Buyer identity and residence as typed on a pre-qualification form.
 *
 * Every field composes a shared field schema from `schemas/common`, so a form
 * and the wire it posts to derive their requiredness from one definition rather
 * than drifting apart. The address is nested and uses `addressSchema` verbatim.
 *
 * `dob` is epoch milliseconds and may be negative: a birthdate before
 * 1970-01-01 belongs to a large share of adult buyers.
 *
 * `ssn` and `annualIncome` are optional here because only the Freedom Road
 * co-brand form collects them; that form and the Finio decision require them.
 * Neither is persisted: the submission and the buyer record never carry them.
 */
const prequalBuyerSchema = z.object({
    firstName: personNameSchema,
    middleName: middleNameSchema.optional(),
    lastName: personLastNameSchema,
    email: emailSchema,
    phone: phoneSchema,
    dob: dateOfBirthSchema,
    address: addressSchema,
    ssn: ssnSchema.optional(),
    annualIncome: annualIncomeSchema.optional(),
});
/**
 * What the buyer actually accepted, as captured.
 *
 * The audit trio (verbatim disclosure text, timestamp, form URL) rides only
 * with an affirmative consent, so a surface with no consent checkbox sends
 * `granted: false` and nothing else rather than a fabricated disclosure.
 */
const prequalConsentSchema = tcpaConsentMetadataSchema.partial().extend({
    granted: z.boolean(),
    // The FCRA authorization the buyer gave by pressing Get pre-qualified, as
    // shown, and the version of that wording. Separate from the TCPA consent
    // above: pressing the button authorizes the soft pull whether or not the
    // buyer accepted contact.
    fcraDisclosureText: z.string().optional(),
    fcraDisclosureVersion: z.string().optional(),
});
/**
 * The longest the server spends reaching a vendor prequal decision: the vendor
 * submit, bounded by its own 15s request timeout, then up to 10s waiting on the
 * decision. The soft pull runs beside them inside the same budget. A client
 * waiting on a vendor prequal derives its timeout from this.
 */
const PREQUAL_DECISION_BUDGET_MS = 25_000;
/**
 * The buyer's acceptance of a prequal vendor's own terms, captured on the
 * Freedom Road co-brand form. Only that form sends it, and the vendor decision
 * runs only when all three are true.
 */
const prequalVendorConsentSchema = z.object({
    termsOfService: z.boolean(),
    privacyPolicy: z.boolean(),
    electronicRecordsAndSignatures: z.boolean(),
});
/**
 * The vehicle the buyer was on, when there is one. All three references are
 * optional and none substitutes for another: `inventoryItemId` is the physical
 * unit, `vehicleProductId` the canon configuration, and `oemId` the catalog it
 * lives in. A pre-qual from a banner or a chat thread carries none of them.
 *
 * `oemId` rides with `vehicleProductId` because nothing else on a submission
 * names the catalog: `svpId` is the dealer rooftop and `programOwnerId` is a
 * ServicesProvider id, not the key canon vehicles are filed under.
 */
const prequalCollateralSchema = z.object({
    vehicleProductId: z.string().min(1).optional(),
    inventoryItemId: z.string().min(1).optional(),
    oemId: z.string().min(1).optional(),
});
/** Which rooftop ran the pre-qual and which program owner's policy governs it. */
const prequalTenantSchema = z.object({
    svpId: z.string().min(1),
    programOwnerId: z.string().min(1),
    /**
     * The rooftop a group widget is anchored to, when it differs from `svpId`.
     * Server-set like the rest of the tenant. The Freedom Road route checks the
     * unit against it first, matching the rooftop the widget chose the form for.
     */
    rooftopSvpId: z.string().min(1).optional(),
});
/**
 * Which surface asked for the pre-qualification. Every surface calls the one
 * prequal service, so this is the only thing that lets the authoritative
 * outcome record be split by where the buyer was. The values mirror the enum on
 * the `financing_prequal_submitted` analytics event, which this field populates.
 * A caller that omits it is reported as `unknown` by the service rather than
 * crossing the wire absent and failing the event's schema.
 */
const PREQUAL_REQUEST_SURFACES = [
    'checkout',
    'express',
    'sales-agent',
    'carthage',
    'unknown',
];
const prequalRequestSurfaceSchema = z.enum(PREQUAL_REQUEST_SURFACES);
/**
 * The one pre-qualification input contract. Every surface's form, every wire
 * payload, and every backend validator derive their requiredness from here.
 */
const prequalContractSchema = z.object({
    buyer: prequalBuyerSchema,
    consent: prequalConsentSchema,
    vendorConsent: prequalVendorConsentSchema.optional(),
    collateral: prequalCollateralSchema.default({}),
    tenant: prequalTenantSchema,
    requestSurface: prequalRequestSurfaceSchema.optional(),
});

export { PREQUAL_DECISION_BUDGET_MS, PREQUAL_REQUEST_SURFACES, prequalBuyerSchema, prequalCollateralSchema, prequalConsentSchema, prequalContractSchema, prequalRequestSurfaceSchema, prequalTenantSchema, prequalVendorConsentSchema };
//# sourceMappingURL=contract.js.map
